import React, { ButtonHTMLAttributes, ReactNode } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    /**
     * The variant of the button
     * - primary: Teal background with white text (default)
     * - secondary: White background with border
     * - outline: Transparent background with border
     */
    variant?: 'primary' | 'secondary' | 'outline';

    /**
     * The size of the button
     * - sm: Small
     * - md: Medium (default)
     * - lg: Large
     */
    size?: 'sm' | 'md' | 'lg';

    /**
     * Icon to display before the text
     */
    leftIcon?: ReactNode;

    /**
     * Icon to display after the text
     */
    rightIcon?: ReactNode;

    /**
     * Loading state
     */
    loading?: boolean;

    /**
     * Full width button
     */
    fullWidth?: boolean;

    /**
     * Children/text content
     */
    children: ReactNode;
}

const getButtonClasses = (variant: 'primary' | 'secondary' | 'outline' = 'primary', size: 'sm' | 'md' | 'lg' = 'md'): string => {
    const baseClasses = 'inline-flex items-center justify-center font-semibold transition-colors';
    const sizeClasses = {
        sm: 'px-3 py-2 text-xs rounded-lg',
        md: 'px-4 py-3 text-sm rounded-lg',
        lg: 'px-6 py-4 text-base rounded-lg',
    };

    const variantClasses = {
        primary: 'bg-[#08b4bd] text-white shadow-[0px_2px_5px_0px_rgba(0,0,0,0.2)] hover:bg-[#0699a1]',
        secondary: 'bg-white text-[#3d4966] border border-[#42cbd2] hover:bg-gray-50',
        outline: 'bg-transparent text-[#08b4bd] border border-[#08b4bd] hover:bg-[rgba(8,180,189,0.05)]',
    };

    return `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]}`;
};

/**
 * Standardized Button Component
 *
 * A reusable button component that follows the Figma design system specifications.
 * Supports multiple variants, sizes, icons, and states.
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="md">
 *   Click me
 * </Button>
 *
 * <Button variant="secondary" leftIcon={<Icon />}>
 *   With Icon
 * </Button>
 *
 * <Button variant="outline" loading>
 *   Loading...
 * </Button>
 * ```
 */
export const Button: React.FC<ButtonProps> = ({
    variant = 'primary',
    size = 'md',
    leftIcon,
    rightIcon,
    loading = false,
    fullWidth = false,
    disabled = false,
    className = '',
    children,
    ...props
}) => {
    const baseClasses = getButtonClasses(variant, size);
    const widthClass = fullWidth ? 'w-full' : '';
    const disabledClass = disabled || loading ? 'opacity-50 cursor-not-allowed' : '';
    const combinedClassName = `${baseClasses} ${widthClass} ${disabledClass} ${className}`.trim();

    return (
        <button
            className={combinedClassName}
            disabled={disabled || loading}
            {...props}
        >
            {loading && (
                <svg
                    className="animate-spin h-4 w-4 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                    ></circle>
                    <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                </svg>
            )}
            {!loading && leftIcon && <span className="mr-2">{leftIcon}</span>}
            {children}
            {!loading && rightIcon && <span className="ml-2">{rightIcon}</span>}
        </button>
    );
};

export default Button;